import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/lib/supabase';
import { Camera, Loader2 } from 'lucide-react';

interface LogoUploadProps {
  companyId: string;
  onUploadComplete: (url: string) => void;
}

export function LogoUpload({ companyId, onUploadComplete }: LogoUploadProps) {
  const { toast } = useToast();
  const [uploading, setUploading] = useState(false);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    try {
      if (!event.target.files || event.target.files.length === 0) {
        return;
      }

      const file = event.target.files[0];
      const fileSize = file.size / 1024 / 1024; // Convert to MB

      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast({
          variant: 'destructive',
          title: 'Invalid file type',
          description: 'Please upload an image file (PNG, JPG, or SVG).',
        });
        return;
      }

      // Validate file size (5MB limit)
      if (fileSize > 5) {
        toast({
          variant: 'destructive',
          title: 'File too large',
          description: 'Logo must be less than 5MB.',
        });
        return;
      }

      setUploading(true);

      // First, remove any existing logo
      const { data: existingFiles } = await supabase.storage
        .from('company_logos')
        .list(companyId);

      if (existingFiles && existingFiles.length > 0) {
        await supabase.storage
          .from('company_logos')
          .remove(existingFiles.map(file => `${companyId}/${file.name}`));
      }

      // Generate unique filename with timestamp
      const timestamp = Date.now();
      const fileExt = file.name.split('.').pop();
      const filePath = `${companyId}/logo-${timestamp}.${fileExt}`;

      // Upload new logo
      const { data, error: uploadError } = await supabase.storage
        .from('company_logos')
        .upload(filePath, file, {
          upsert: true,
          contentType: file.type,
          cacheControl: '0'
        });

      if (uploadError) {
        if (uploadError.message.includes('Permission denied')) {
          throw new Error('You do not have permission to upload logos for this company.');
        }
        throw uploadError;
      }

      // Get public URL
      const { data: { publicUrl }, error: urlError } = supabase.storage
        .from('company_logos')
        .getPublicUrl(filePath);

      if (urlError) throw urlError;

      // Add cache busting parameter
      const publicUrlWithCache = `${publicUrl}?t=${timestamp}`;

      // Update company with new logo URL
      const { error: updateError } = await supabase
        .from('companies')
        .update({
          logo_url: publicUrlWithCache,
          updated_at: new Date().toISOString()
        })
        .eq('id', companyId);

      if (updateError) throw updateError;

      // Update UI
      onUploadComplete(publicUrlWithCache);

      toast({
        title: 'Success',
        description: 'Company logo updated successfully',
      });
    } catch (error: any) {
      console.error('Error uploading logo:', error);
      toast({
        variant: 'destructive',
        title: 'Error',
        description: error.message || 'Failed to upload logo. Please try again.',
      });
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="absolute bottom-0 right-0">
      <div className="relative">
        <input
          type="file"
          accept="image/png,image/jpeg,image/jpg,image/svg+xml"
          onChange={handleFileChange}
          disabled={uploading}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
          aria-label="Upload logo"
        />
        <Button 
          size="icon" 
          variant="secondary" 
          className="h-8 w-8 rounded-full"
          disabled={uploading}
        >
          {uploading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <Camera className="h-4 w-4" />
          )}
        </Button>
      </div>
    </div>
  );
}